import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Email } from '@kula-ai/components';
import type {
  EditorProps,
  EmailCompositionValue,
  EmailProps,
} from '@kula-ai/components';
import type { SelectOption } from '@kula-ai/components';
import { useShowToast } from '@/hooks';
import { customTemplateSelectProps } from '@/components/shared/customTemplateLabel';
import { isTemplateEdited } from '@/components/shared/email/templateSelection';
import { DEFAULT_ATTACHMENT_FILES } from '@/constants/attachment-files';
import {
  applyTemplateMetadataOnChange,
  mapTemplateAttachmentsToEditorItems,
} from '../InterviewActivity/communication/helpers/templateApply';
import type {
  InterviewEditorAttachmentItem,
  InterviewVariablesByCategory,
} from '../InterviewActivity/communication/types';

// Only a 14.9 MB total cap is meaningful — enforced in onAttachFiles below.
// High count + per-file pass-through caps stand in for "unlimited" so kula
// doesn't silently reject anything before our total-size guard can toast.
const MAX_ATTACHMENT_FILES = 99;
const MAX_TOTAL_ATTACHMENT_SIZE_MB = 14.9;
const KULA_PER_FILE_PASSTHROUGH_MB = 100;
const ATTACHMENT_ACCEPT = DEFAULT_ATTACHMENT_FILES.join(',');
const ATTACHMENT_TOOLTIP = 'Upload files less than 15Mb in size';
const BYTES_PER_MB = 1024 * 1024;
// Signature is a multi-line HTML block for the body footer, never a
// single-line subject — hide it from the subject picker and validation.
const SUBJECT_EXCLUDED_VARIABLE_KEYS = ['sender.email_signature'] as const;
// An empty template id is a real value, so "not seeded" needs its own marker.
const NOT_SEEDED = Symbol('not-seeded');

function interviewFileToAttachmentItem(
  file: File
): InterviewEditorAttachmentItem {
  return {
    key: `${file.name}-${file.size}-${file.lastModified}`,
    name: file.name,
    size: file.size,
    mimeType: file.type,
    file,
  };
}

export type InterviewEmailEditorVariant = 'calendarInvite' | 'richEmail';

export type TemplatedEmailProps = Omit<
  EmailProps,
  'onChange' | 'templateOptions'
> & {
  templateOptions: SelectOption[];
  value: EmailCompositionValue;
  onChange: (_value: EmailCompositionValue) => void;
  editorVariant?: InterviewEmailEditorVariant;
  variables?: InterviewVariablesByCategory;
  attachments?: InterviewEditorAttachmentItem[];
  onAttachmentsChange?: (_items: InterviewEditorAttachmentItem[]) => void;
  /**
   * Bumped per submit click so the underlying `Email` re-fires its
   * in-editor auto-scroll on repeat submits with unchanged errors.
   * Mirrors the `EmailProps.submitAttempt` contract — declared here
   * explicitly so a stale `@kula-ai/components` types view (TS server
   * caching pre-1.10.60 EmailProps without this field) doesn't make
   * consumers fail to type-check.
   */
  submitAttempt?: number | string;
  /**
   * Host inline-image upload (e.g. POST `/user_image_uploads`). When
   * present, the editor uploads picked files and inserts the returned
   * URL; when absent, the underlying editor falls back to a base64 data
   * URL that bloats the saved `*_comm_config.body` payload. Pass it for
   * both calendar-invite and rich-email variants.
   */
  uploadInlineImage?: EditorProps['uploadInlineImage'];
};

export function TemplatedEmail({
  templateOptions,
  value,
  onChange,
  editorProps,
  editorVariant = 'calendarInvite',
  variables,
  attachments,
  onAttachmentsChange,
  uploadInlineImage,
  subjectVariableInputProps,
  templateSelectProps,
  ...rest
}: TemplatedEmailProps) {
  const toast = useShowToast();
  const prev = useRef(value);
  useEffect(() => {
    prev.current = value;
  }, [value]);

  const handleChange = useCallback(
    (next: EmailCompositionValue) => {
      const merged = applyTemplateMetadataOnChange(
        prev.current,
        next,
        templateOptions
      );
      prev.current = merged;
      onChange(merged);
    },
    [onChange, templateOptions]
  );

  // Seed the selected template's attachments into user-controlled state.
  const seededTemplate = useRef<string | null | undefined | typeof NOT_SEEDED>(
    NOT_SEEDED
  );
  useEffect(() => {
    if (!onAttachmentsChange) return;
    const templateId = value.template;
    if (templateId === seededTemplate.current) return;
    const initial = seededTemplate.current === NOT_SEEDED;

    // Template cleared — drop its attachments, keep user files.
    if (!templateId) {
      seededTemplate.current = templateId;
      if (!initial) {
        onAttachmentsChange((attachments ?? []).filter((a) => a.file));
      }
      return;
    }

    // The id is prefilled before the list resolves; wait for the options.
    if (!templateOptions.length) return;

    seededTemplate.current = templateId;
    const opt = templateOptions.find((o) => o.value === templateId);
    if (!opt) return;

    // At mount the template is already selected, so only fill an empty list —
    // a restored config keeps what it saved.
    if (initial && (attachments ?? []).length) return;

    const templateItems = mapTemplateAttachmentsToEditorItems(
      opt.data?.attachments
    ) as InterviewEditorAttachmentItem[];
    const userFiles = (attachments ?? []).filter((a) => a.file);
    onAttachmentsChange([...templateItems, ...userFiles]);
  }, [value.template, templateOptions, attachments, onAttachmentsChange]);

  const toolbarFeatures = useMemo(
    () =>
      editorVariant === 'calendarInvite'
        ? // Google/Outlook/Apple calendar descriptions strip inline styling
          // (text alignment, font size/family), so keep those controls off.
          {
            image: false,
            attachment: false,
            align: false,
            fontSize: false,
            fontFamily: false,
          }
        : // Rich email is delivered as real HTML — offer font size/family
          // (mirrors the `useEmailField` send composers).
          {
            image: true,
            attachment: true,
            fontSize: true,
            fontFamily: true,
          },
    [editorVariant]
  );

  const mergedEditorProps = useMemo(() => {
    const allAttachments =
      editorVariant === 'richEmail' && attachments?.length
        ? attachments
        : undefined;

    const attachmentHandlers =
      editorVariant === 'richEmail' && onAttachmentsChange
        ? {
            onAttachFiles: (files: File[]) => {
              const existingBytes = (attachments ?? []).reduce(
                (sum, a) => sum + (a.size || 0),
                0
              );
              const newBytes = files.reduce((sum, f) => sum + f.size, 0);
              const totalMb = (existingBytes + newBytes) / BYTES_PER_MB;
              if (totalMb > MAX_TOTAL_ATTACHMENT_SIZE_MB) {
                toast({
                  id: 'attachment-total-size-error',
                  title: 'Total file size exceeds limit',
                  description: `The total size of all attachments should be less than ${Math.round(
                    MAX_TOTAL_ATTACHMENT_SIZE_MB
                  )} MB. Please remove some files or choose smaller files.`,
                  status: 'error',
                });
                return;
              }
              const added = files.map(interviewFileToAttachmentItem);
              onAttachmentsChange([...(attachments ?? []), ...added]);
            },
            onRemoveAttachment: (key: string) => {
              onAttachmentsChange(
                (attachments ?? []).filter((a) => a.key !== key)
              );
            },
          }
        : {};

    // Forwarded to `Editor` for both variants. Calendar invites hide the
    // toolbar image button via `toolbarFeatures.image: false`, but the
    // node-view's in-place replace input and any future paste/drop hook
    // still need a host upload path so base64 doesn't end up on the wire.
    const inlineImageProp = uploadInlineImage ? { uploadInlineImage } : {};

    const variableConfig = variables
      ? { ...editorProps?.variable, vars: variables }
      : editorProps?.variable;

    // Consumer-supplied overrides win.
    const attachmentCaps =
      editorVariant === 'richEmail'
        ? {
            attachmentMaxFiles:
              editorProps?.attachmentMaxFiles ?? MAX_ATTACHMENT_FILES,
            attachmentMaxFileSizeMb:
              editorProps?.attachmentMaxFileSizeMb ??
              KULA_PER_FILE_PASSTHROUGH_MB,
            attachmentAccept:
              editorProps?.attachmentAccept ?? ATTACHMENT_ACCEPT,
            attachmentTooltip:
              editorProps?.attachmentTooltip ?? ATTACHMENT_TOOLTIP,
          }
        : {};

    return {
      ...editorProps,
      ...(variableConfig ? { variable: variableConfig } : {}),
      toolbarFeatures: {
        ...editorProps?.toolbarFeatures,
        ...toolbarFeatures,
      },
      ...attachmentCaps,
      ...(allAttachments ? { attachments: allAttachments } : {}),
      ...attachmentHandlers,
      ...inlineImageProp,
    };
  }, [
    attachments,
    editorProps,
    editorVariant,
    onAttachmentsChange,
    toast,
    toolbarFeatures,
    uploadInlineImage,
    variables,
  ]);

  const mergedSubjectVariableInputProps = useMemo(() => {
    const consumerExclude = subjectVariableInputProps?.variable?.exclude ?? [];
    const exclude = Array.from(
      new Set([...consumerExclude, ...SUBJECT_EXCLUDED_VARIABLE_KEYS])
    );
    const hasVars = variables && Object.keys(variables).length > 0;
    return {
      ...subjectVariableInputProps,
      variable: {
        ...subjectVariableInputProps?.variable,
        ...(hasVars ? { vars: variables } : {}),
        exclude,
      },
    };
  }, [subjectVariableInputProps, variables]);

  // The closed picker reads "Custom" once the block's copy leaves the template.
  // Derive the flag separately from the props object: `value` changes on every
  // keystroke, and rebuilding the object here would hand kula's template
  // `Select` a new identity each time.
  const templateEdited = useMemo(() => {
    const selected = templateOptions.find(
      (option) => option.value === value.template
    );
    return isTemplateEdited(value, selected, {
      compareSubject: rest.showSubject !== false,
    });
  }, [rest.showSubject, templateOptions, value]);

  const mergedTemplateSelectProps = useMemo(
    () => ({
      ...templateSelectProps,
      ...customTemplateSelectProps(templateEdited),
    }),
    [templateSelectProps, templateEdited]
  );

  return (
    <Email
      // Multiple editors on one page: the kula `Email` auto-scroll uses a
      // module-level dedupe guard, so with several instances only one wins
      // (and it can be suppressed after a tab switch). We disable it and drive
      // the scroll explicitly via `apiRef.focusFirstError()` (which bypasses
      // the guard) from the step's `validate()`. Override via `...rest`.
      scrollToErrorOnValidate={false}
      {...rest}
      editorProps={mergedEditorProps}
      subjectVariableInputProps={mergedSubjectVariableInputProps}
      templateSelectProps={mergedTemplateSelectProps}
      templateOptions={templateOptions}
      value={value}
      onChange={handleChange}
    />
  );
}
